'use client';

import React, { useState } from 'react';
import { Copy, Check, Sparkles, MessageSquare, HelpCircle, Lightbulb, PlayCircle, ArrowRight, FileText } from 'lucide-react';
import type { LessonBlock, AdvancedBlock } from './module1Data';
import Icon from '@/components/ui/AppIcon';
import LivePromptBlock from './LivePromptBlock';


// ─── Prompt Block ─────────────────────────────────────────────────────────────

function PromptBlock({ content, dark = false, label, instruction }: { content: string; dark?: boolean; label?: string; instruction?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <div
      className="rounded-xl p-7 relative"
      style={{ backgroundColor: dark ? 'rgba(255,255,255,0.07)' : 'var(--navy)', border: dark ? '1px solid rgba(255,255,255,0.12)' : 'none' }}
    >
      <div className="flex items-start gap-3 mb-3">
        <Sparkles size={15} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em' }}
        >
          {label || 'The Prompt'}
        </span>
      </div>

      <p
        className="font-body text-sm leading-relaxed mb-4 pr-10"
        style={{ color: 'rgba(255,255,255,0.88)', lineHeight: '1.85' }}
      >
        &ldquo;{content}&rdquo;
      </p>

      <span
        className="inline-block w-0.5 h-4 align-middle mb-4"
        style={{ backgroundColor: 'var(--amber)' }}
        aria-hidden="true"
      />

      <div className="flex items-center justify-between">
        <p className="font-body text-xs italic" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {instruction || 'Copy this and paste it directly into Copilot.'}
        </p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 active:scale-95"
          style={{
            backgroundColor: copied ? 'rgba(45,122,45,0.3)' : 'rgba(255,255,255,0.1)',
            color: copied ? '#7FBF7F' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${copied ? 'rgba(45,122,45,0.4)' : 'rgba(255,255,255,0.15)'}`,
          }}
          aria-label={copied ? 'Prompt copied' : 'Copy prompt to clipboard'}
        >
          {copied ? (
            <><Check size={13} aria-hidden="true" />Copied</>
          ) : (
            <><Copy size={13} aria-hidden="true" />Copy</>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Block config ─────────────────────────────────────────────────────────────

const blockConfig = {
  situation: {
    label: 'The Situation',
    icon: MessageSquare,
    bg: '#EEF1F7',
    border: '#D4DBEA',
    labelColor: 'var(--navy-muted)',
    iconColor: 'var(--navy)',
  },
  why: {
    label: 'Why It Works',
    icon: HelpCircle,
    bg: '#F3F0EC',
    border: 'var(--border)',
    labelColor: 'var(--text-muted)',
    iconColor: 'var(--text-muted)',
  },
  anna: {
    label: "Anna\'s Note",
    icon: Lightbulb,
    bg: 'var(--amber-pale)',
    border: 'rgba(212,137,26,0.2)',
    labelColor: 'var(--amber)',
    iconColor: 'var(--amber)',
  },
  try: {
    label: 'Try It',
    icon: PlayCircle,
    bg: '#EBF5EB',
    border: '#C6E0C6',
    labelColor: '#2D7A2D',
    iconColor: '#2D7A2D',
  },
  example: {
    label: 'Example Output',
    icon: FileText,
    bg: '#F8F9FB',
    border: '#C8D0E0',
    labelColor: '#4A5568',
    iconColor: '#4A5568',
  },
};

// ─── Regular Lesson Content ───────────────────────────────────────────────────

interface LessonContentProps {
  lessonId: string;
  lessonNumber: string;
  lessonTitle: string;
  blocks: LessonBlock[];
  onNextLesson: () => void;
  isLastLesson: boolean;
  useLivePrompt?: boolean;
}

export function LessonContentBlock({ lessonId, lessonNumber, lessonTitle, blocks, onNextLesson, isLastLesson, useLivePrompt = false }: LessonContentProps) {
  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      {/* Lesson header */}
      <div className="mb-10">
        <span
          className="inline-block font-body text-xs uppercase tracking-widest mb-3"
          style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
        >
          Lesson {lessonNumber}
        </span>
        <h1
          className="font-display font-semibold mb-4"
          style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', lineHeight: '1.2' }}
        >
          {lessonTitle}
        </h1>
        <div className="h-0.5 w-12 rounded-full" style={{ backgroundColor: 'var(--amber)' }} aria-hidden="true" />
      </div>

      {/* Lesson blocks */}
      <div className="flex flex-col gap-6">
        {blocks.map((block, i) => {
          // For lesson-1-2 with live prompt: replace 'try' block with LivePromptBlock
          if (useLivePrompt && block.type === 'try') {
            return (
              <div key={`${lessonId}-block-${i}`}>
                <LivePromptBlock />
              </div>
            );
          }

          if (block.type === 'prompt') {
            return <PromptBlock key={`${lessonId}-block-${i}`} content={block.content} label={block.label} instruction={block.instruction} />;
          }

          const config = blockConfig[block.type as keyof typeof blockConfig];
          if (!config) return null;
          const Icon = config.icon;

          return (
            <div
              key={`${lessonId}-block-${i}`}
              className="rounded-xl p-7"
              style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={14} style={{ color: config.iconColor }} aria-hidden="true" />
                <span
                  className="font-body text-xs uppercase tracking-widest font-semibold"
                  style={{ color: config.labelColor, letterSpacing: '0.1em' }}
                >
                  {block.label || config.label}
                </span>
              </div>
              <p
                className="font-body text-sm leading-relaxed whitespace-pre-line"
                style={{
                  color: block.type === 'example' ? '#374151' : '#374151',
                  lineHeight: '1.85',
                  fontFamily: block.type === 'example' ? 'var(--font-mono, monospace)' : undefined,
                  fontSize: block.type === 'example' ? '0.8125rem' : undefined,
                }}
              >
                {block.content}
              </p>
            </div>
          );
        })}
      </div>

      {/* Next lesson button */}
      <div className="mt-12 pt-8 border-t flex justify-end" style={{ borderColor: 'var(--border)' }}>
        <button
          onClick={onNextLesson}
          className="inline-flex items-center gap-2 font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95"
          style={{ backgroundColor: 'var(--navy)', color: 'white' }}
        >
          {isLastLesson ? 'Complete Module 1' : 'Next lesson'}
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ─── Advanced Lesson Block ────────────────────────────────────────────────────

const advancedBlockConfig = {
  situation: {
    label: 'The Situation',
    icon: MessageSquare,
    border: 'rgba(255,255,255,0.1)',
    labelColor: 'rgba(255,255,255,0.4)',
    iconColor: 'rgba(255,255,255,0.4)',
    textColor: 'rgba(255,255,255,0.75)',
    bg: 'rgba(255,255,255,0.05)',
  },
  anna: {
    label: "Anna\'s Note",
    icon: Lightbulb,
    border: 'rgba(212,137,26,0.3)',
    labelColor: 'var(--amber)',
    iconColor: 'var(--amber)',
    textColor: 'rgba(255,255,255,0.8)',
    bg: 'rgba(212,137,26,0.08)',
  },
};

interface AdvancedLessonBlockProps {
  letter: string;
  title: string;
  blocks: AdvancedBlock[];
}

export function AdvancedLessonBlock({ letter, title, blocks }: AdvancedLessonBlockProps) {
  return (
    <div className="mb-14">
      {/* Advanced lesson header */}
      <div className="mb-8">
        <span
          className="inline-block font-body text-xs uppercase tracking-widest mb-3"
          style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
        >
          Advanced — {letter}
        </span>
        <h3
          className="font-display font-semibold mb-4"
          style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', color: 'white', lineHeight: '1.25' }}
        >
          {title}
        </h3>
        <div className="h-0.5 w-10 rounded-full" style={{ backgroundColor: 'var(--amber)' }} aria-hidden="true" />
      </div>

      {/* Advanced blocks */}
      <div className="flex flex-col gap-6">
        {blocks.map((block, i) => {
          if (block.type === 'prompt') {
            return <PromptBlock key={`adv-${letter}-${i}`} content={block.content} dark />;
          }

          const config = advancedBlockConfig[block.type as keyof typeof advancedBlockConfig];
          if (!config) return null;
          const Icon = config.icon;

          return (
            <div
              key={`adv-${letter}-${i}`}
              className="rounded-xl p-7"
              style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={14} style={{ color: config.iconColor }} aria-hidden="true" />
                <span
                  className="font-body text-xs uppercase tracking-widest font-semibold"
                  style={{ color: config.labelColor, letterSpacing: '0.1em' }}
                >
                  {config.label}
                </span>
              </div>
              <p className="font-body text-sm leading-relaxed" style={{ color: config.textColor, lineHeight: '1.85' }}>
                {block.content}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
